import { useLayoutEffect, useRef } from 'react'

import UIIcon, { UIIconType } from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'

type SummaryBackgroundProps = React.HTMLProps<HTMLDivElement> & {
  is?: 'div' | 'section' | 'footer'
  classNameInner?: string
  background: UIIconType
  isLast?: boolean
  debug?: boolean
}

export default function SummaryBackground({
  children,
  className,
  classNameInner,
  background,
  debug,
  is = 'div',
  isLast = false,
}: SummaryBackgroundProps) {
  const { gsap } = useGSAP()
  const mm = useRef<ReturnType<typeof gsap.matchMedia> | null>(null)

  const parentRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<SVGSVGElement>(null)

  const Component = is

  useLayoutEffect(() => {
    // Reset any previous media contexts and create a new one
    mm.current?.revert()
    mm.current = gsap.matchMedia()

    mm.current.add('(prefers-reduced-motion: reduce)', () => {
      const onResize = () => {
        gsap.set(backgroundRef.current, {
          scale: 2,
          rotate: 0,
          y: 0,
          opacity: 1,
          transformOrigin: '50% 50%',
        })
      }
      window.addEventListener('resize', onResize)
      onResize()
      return () => {
        window.removeEventListener('resize', onResize)
      }
    })

    // No preference: keep the animated ScrollTrigger timeline
    mm.current.add('(prefers-reduced-motion: no-preference)', () => {
      let ctx: gsap.Context | null = null

      const onResize = () => {
        ctx?.revert()
        ctx = gsap.context(() => {
          const scale = () => {
            const baseW = 512
            const wW = window.innerWidth
            const isSmall = wW < 768
            return ((wW * 2.15) / baseW) * (isLast && isSmall ? 2 : 1)
          }

          const timeline = gsap.timeline({
            scrollTrigger: {
              trigger: parentRef.current,
              start: 'top bottom',
              end: isLast ? 'bottom bottom' : 'bottom top',
              scrub: true,
              markers: debug,
            },
          })

          timeline.fromTo(
            backgroundRef.current,
            { scale: 0, rotate: -45 },
            { scale, rotate: 45, duration: 0.6, ease: 'power1.in' }
          )

          if (!isLast) {
            timeline.fromTo(backgroundRef.current, { y: '0vh' }, { y: '100vh', duration: 0.5, ease: 'none' }, 0.5)
          }

          timeline.fromTo(backgroundRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0)

          if (!isLast) {
            timeline.to(backgroundRef.current, { opacity: 0, duration: 0.5, ease: 'power4.out' }, 0.5)
          }
        })
      }

      window.addEventListener('resize', onResize)
      onResize()

      return () => {
        window.removeEventListener('resize', onResize)
        ctx.revert()
      }
    })
    return () => {
      mm.current?.revert()
    }
  }, [debug, isLast, parentRef, backgroundRef, gsap])

  return (
    <Component ref={parentRef} className={`SummaryBackground relative w-full ${className ? className : ''}`}>
      <div
        aria-hidden='true'
        className='absolute z-1 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none'
      >
        <UIIcon ref={backgroundRef} icon={background} className='text-512 text-secondary' />
      </div>
      <div className={`relative z-2 ${classNameInner ? classNameInner : ''}`}>{children}</div>
    </Component>
  )
}
